import { createElement, type ButtonHTMLAttributes, type ReactNode, type Ref, type SVGAttributes } from 'react';
import { cx } from './cx';
import { ICONS, type IconName } from './icons';

export interface IconProps extends Omit<SVGAttributes<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  /** An icon that means something on its own gets a name; a decorative one is hidden from screen readers. */
  label?: string;
}

export function Icon({ name, size = 24, strokeWidth = 2, className, label, ...rest }: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cx('zb-icon', className)}
      aria-hidden={label ? undefined : true}
      role={label ? 'img' : undefined}
      aria-label={label}
      {...rest}
    >
      {ICONS[name].map(([tag, attrs], i) => createElement(tag, { key: i, ...attrs }))}
    </svg>
  );
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'md' | 'lg';
  icon?: IconName;
  iconRight?: IconName;
  block?: boolean;
  ref?: Ref<HTMLButtonElement>;
}

/** A pill with a 2px ink border, 48 px tall (56 with size="lg"). One primary per screen; labels never wrap. */
export function Button({ variant = 'primary', size = 'md', icon, iconRight, block, children, className, type = 'button', ...rest }: ButtonProps) {
  return (
    <button type={type} className={cx('zb-btn', `zb-btn--${variant}`, `zb-btn--${size}`, block && 'zb-btn--block', className)} {...rest}>
      {icon && <Icon name={icon} size={20} />}
      <span>{children}</span>
      {iconRight && <Icon name={iconRight} size={20} />}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Required: the button's accessible name. */
  label: string;
  variant?: 'plain' | 'outlined' | 'filled';
  badge?: number | string | undefined;
  ref?: Ref<HTMLButtonElement>;
}

export function IconButton({ icon, label, variant = 'plain', badge, className, ...rest }: IconButtonProps) {
  return (
    <button type="button" aria-label={label} title={label} className={cx('zb-iconbtn', `zb-iconbtn--${variant}`, className)} {...rest}>
      <Icon name={icon} size={24} />
      {badge ? (
        <span className="zb-iconbtn__dot" aria-hidden="true">
          {badge}
        </span>
      ) : null}
    </button>
  );
}

const BADGE_ICONS: Partial<Record<NonNullable<BadgeProps['tone']>, IconName>> = { leaf: 'check', pepper: 'triangle-alert', indigo: 'info' };

export interface BadgeProps {
  tone?: 'neutral' | 'palm' | 'clay' | 'leaf' | 'pepper' | 'indigo';
  icon?: IconName | false;
  children: ReactNode;
  className?: string;
}

/** A short status. Success, danger and info carry a glyph, so colour is never the only signal. */
export function Badge({ tone = 'neutral', icon, children, className }: BadgeProps) {
  const glyph = icon === false ? null : (icon ?? BADGE_ICONS[tone]);
  return (
    <span className={cx('zb-badge', `zb-badge--${tone}`, className)}>
      {glyph && <Icon name={glyph} size={14} strokeWidth={2.5} />}
      {children}
    </span>
  );
}

const BANNER_ICONS: Record<NonNullable<BannerProps['tone']>, IconName> = {
  info: 'info',
  warning: 'triangle-alert',
  danger: 'triangle-alert',
  success: 'check',
  offline: 'wifi-off',
  tax: 'landmark',
};

export interface BannerProps {
  tone?: 'info' | 'warning' | 'danger' | 'success' | 'offline' | 'tax';
  title?: ReactNode;
  children?: ReactNode;
  action?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  className?: string;
}

/** One per screen; the most urgent wins (design system). */
export function Banner({ tone = 'info', title, children, action, onAction, onDismiss, className }: BannerProps) {
  return (
    <div role={tone === 'danger' ? 'alert' : 'status'} className={cx('zb-banner', `zb-banner--${tone}`, className)}>
      <span className="zb-banner__icon">
        <Icon name={BANNER_ICONS[tone]} size={24} />
      </span>
      <div className="zb-banner__body">
        {title && <div className="zb-banner__title">{title}</div>}
        {children && <div className="zb-banner__text">{children}</div>}
        {action && (
          <button type="button" className="zb-link-btn" onClick={onAction}>
            {action}
          </button>
        )}
      </div>
      {onDismiss && <IconButton icon="x" label="Dismiss" onClick={onDismiss} className="zb-banner__close" />}
    </div>
  );
}

export interface SectionHeaderProps {
  title: string;
  action?: string;
  onAction?: () => void;
  level?: 2 | 3;
  className?: string;
}

export function SectionHeader({ title, action, onAction, level = 2, className }: SectionHeaderProps) {
  const Heading = level === 3 ? 'h3' : 'h2';
  return (
    <div className={cx('zb-sechead', className)}>
      <Heading className={level === 3 ? 'zb-sechead__t3' : 'zb-sechead__t2'}>{title}</Heading>
      {action && (
        <button type="button" className="zb-link-btn" onClick={onAction}>
          {action}
        </button>
      )}
    </div>
  );
}

export interface CardProps {
  title?: string;
  action?: string;
  onAction?: () => void;
  padded?: boolean;
  children?: ReactNode;
  className?: string;
}

export function Card({ title, action, onAction, padded = true, children, className }: CardProps) {
  return (
    <section className={cx('zb-card', padded && 'zb-card--padded', className)}>
      {(title || action) && <SectionHeader title={title ?? ''} {...(action ? { action } : {})} {...(onAction ? { onAction } : {})} level={3} />}
      {children}
    </section>
  );
}
